import React, { useState, useEffect } from 'react';
import { Mail, Terminal, Clock, User, CheckCircle, XCircle, RefreshCw, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCommandsPanel = ({ token }) => {
  const [commands, setCommands] = useState([]);
  const [commandList, setCommandList] = useState({});
  const [receiverStatus, setReceiverStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);

  // Charger le statut du receiver
  const fetchReceiverStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/receiver/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReceiverStatus(data);
      }
    } catch (error) {
      console.error('Erreur receiver status:', error);
    }
  };

  // Charger la liste des commandes disponibles
  const fetchCommandList = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/commands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCommandList(data.commands || {});
      }
    } catch (error) {
      console.error('Erreur commands list:', error);
    }
  };

  // Charger l'historique des commandes
  const fetchCommandLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-commands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Erreur command logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    setLoading(true);
    fetchReceiverStatus();
    fetchCommandList();
    fetchCommandLogs();
  };

  useEffect(() => {
    refresh();
  }, [token]);

  return (
    <div className="bg-black border-2 border-purple-500/50" data-testid="email-commands-panel">
      {/* Header */}
      <div className="bg-purple-900/30 px-4 py-3 border-b border-purple-500/50 flex items-center justify-between">
        <span className="font-mono text-purple-400 text-sm uppercase tracking-wider flex items-center gap-2">
          <Terminal className="w-4 h-4" />
          Commandes Email (Télécommande)
        </span>
        <button
          onClick={refresh}
          className="text-purple-400 hover:text-white"
          title="Actualiser"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Receiver Status */}
        {receiverStatus && (
          <div className="flex items-center justify-between p-3 bg-purple-900/20 border border-purple-500/30 rounded">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${receiverStatus.running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <div>
                <p className="font-mono text-white text-sm">
                  Service IMAP: {receiverStatus.running ? 'Actif' : 'Inactif'}
                </p>
                <p className="font-mono text-white/80 text-xs">
                  {receiverStatus.imap_user} - Polling: {receiverStatus.polling_interval}s
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono text-purple-400 text-sm">
                {receiverStatus.registered_commands?.length || 0} commandes
              </p>
            </div>
          </div>
        )}

        {/* Commands Available */}
        <div>
          <h4 className="font-mono text-white/90 text-xs uppercase mb-2">Commandes Disponibles</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(commandList).map(([cmd, info]) => (
              <div key={cmd} className="p-2 bg-purple-900/10 border border-purple-500/20 rounded">
                <p className="font-mono text-purple-400 text-sm font-bold">{cmd.toUpperCase()}</p>
                <p className="font-mono text-white/80 text-xs">{info.description}</p>
                <p className="font-mono text-white/30 text-xs">ETA: {info.eta_minutes} min</p>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded">
          <p className="font-mono text-blue-400 text-xs uppercase mb-2">Mode d'emploi</p>
          <p className="font-mono text-white/90 text-sm">
            Envoyez un email à <span className="text-blue-400">analyste@osint-marine.fr</span> avec la commande dans le sujet.
          </p>
          <p className="font-mono text-white/80 text-xs mt-2">
            Exemples: "SCAN hormuz", "BRIEF russie", "RAPPORT", "ALERTES"
          </p>
        </div>

        {/* Command History */}
        <div>
          <h4 className="font-mono text-white/90 text-xs uppercase mb-2">Historique des Commandes</h4>
          <div className="bg-black/50 border border-purple-500/20 max-h-64 overflow-y-auto">
            {logs.length > 0 ? (
              <table className="w-full">
                <thead className="bg-purple-900/30 sticky top-0">
                  <tr className="font-mono text-white/80 text-xs uppercase">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Expéditeur</th>
                    <th className="text-left p-2">Commande</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-right p-2">Durée</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-500/10">
                  {logs.map((log, idx) => (
                    <tr key={idx} className="hover:bg-purple-900/10">
                      <td className="p-2 font-mono text-white/80 text-xs">
                        {new Date(log.timestamp).toLocaleString('fr-FR', { 
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                        })}
                      </td>
                      <td className="p-2">
                        <p className="font-mono text-white/80 text-xs">{log.user_name}</p>
                        <p className="font-mono text-white/40 text-xs">{log.user_rank}</p>
                      </td>
                      <td className="p-2">
                        <span className="font-mono text-purple-400 text-xs font-bold">
                          {log.command?.toUpperCase()}
                        </span>
                        {log.params && (
                          <span className="font-mono text-white/80 text-xs ml-1">{log.params}</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {log.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 inline" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 inline" />
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-white/80 text-xs">
                        {log.execution_time?.toFixed(1)}s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center">
                <Mail className="w-8 h-8 text-purple-500/30 mx-auto mb-2" />
                <p className="font-mono text-white/80 text-sm">Aucune commande reçue</p>
                <p className="font-mono text-white/30 text-xs mt-1">
                  Les commandes email apparaîtront ici
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCommandsPanel;
